import { getAirbrake } from "@simplybusiness/services";
import { useAtomCallback } from "jotai/utils";
import { useCallback } from "react";
import type { QuestionProps } from "../../components/Questionnaire/Section/Question/types";
import {
  AIRBRAKE_PROJECT_ID,
  AIRBRAKE_PROJECT_KEY,
} from "../../constants/constants";
import { mapToArray, transformAnswers } from "../../utils";
import { apiRequest } from "../../utils/apiRequest";
import { useLocale } from "../useLocale";
import { useMutationStatus } from "../useMutationStatus";
import { useSetApiError } from "../useApiError";
import { useQuestionnaireActions } from "../../features/questionnaire";
import { questionnaireReducerAtom } from "../../features/questionnaire/state/questionnaireReducerAtom";
import type { Answers } from "../../features/questionnaire";
import { useTokenQueue } from "../useTokenQueue";
import type { Tokens } from "../../store";

const airbrake = getAirbrake({
  projectId: AIRBRAKE_PROJECT_ID,
  projectKey: AIRBRAKE_PROJECT_KEY,
});

type QuestionsResponse = {
  data: {
    questions: QuestionProps[];
    configuration: {
      authenticityToken: string;
      journeyToken: string;
    };
  };
};

/**
 * Calls the Questions API with the provided answers
 * while editing assumptions.
 *
 * Features:
 * - Uses shared token queue for sequential execution across all token-using hooks
 * - Prevents stale token issues when interleaving with other requests
 */
export const useDependentQuestions = () => {
  const { enqueue } = useTokenQueue();
  const { appendQuestions } = useQuestionnaireActions();
  const { locale } = useLocale();
  const setApiError = useSetApiError();
  const isSendingAnswers = useMutationStatus("dependentQuestions");

  // Read sections and visibility without subscribing to state changes
  const getQuestionnaireState = useAtomCallback(
    useCallback(get => get(questionnaireReducerAtom), []),
  );

  const sendAnswers = useCallback(
    (answers: Answers, targetQuestion: string): Promise<QuestionsResponse> => {
      return enqueue(async (tokens: Tokens) => {
        try {
          const state = getQuestionnaireState();
          const transformedAnswers = transformAnswers(
            answers,
            state.sections,
            state.visibility,
            locale,
          );
          const answersArray = mapToArray(transformedAnswers);

          const response = (await apiRequest(
            "?step_action=dependent_questions",
            {
              ...tokens,
              answers: answersArray,
              targetQuestion,
            },
          )) as QuestionsResponse;

          // Clear any previous API error on success
          setApiError(null);

          // Append new questions to state
          appendQuestions(response.data.questions);

          return {
            newTokens: response.data.configuration,
            result: response,
          };
        } catch (err) {
          const error = err instanceof Error ? err : new Error(String(err));
          if (error.name !== "AbortError" && error.name !== "TimeoutError") {
            console.error(`Error updating questions: ${error.message}`);
            airbrake.notify(error);
            setApiError("Something went wrong. Please try again.");
          }
          throw error;
        }
      });
    },
    [enqueue, getQuestionnaireState, locale, setApiError, appendQuestions],
  );

  return {
    sendAnswers,
    isSuccess: false, // No longer tracked via mutation - could add if needed
    isPending: isSendingAnswers,
    isError: false, // No longer tracked via mutation - could add if needed
    error: null, // No longer tracked via mutation - could add if needed
  };
};
